import { useState, useEffect } from 'react';
import { IAutoparkCar } from '../../services/autopark.service';
import { CarService, ICarModel } from '../../services/car.service';
import { normalizeImageUrl } from '../../utils/utils';
import { useAppSelector } from '../../store/hooks';
import { formatCarNumber } from '../../utils/carNumberUtils';
import { formatDateForInput } from '../../utils/date.utils';

// Дата из <input type="date"> в формат бэкенда
const convertDateFromInputFormat = (date: string): string => {
  if (!date) return '';
  // Конвертируем из yyyy-MM-dd в dd-MM-yyyy
  const parts = date.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return date;
};

interface CarTableRowProps {
  car: IAutoparkCar;
  expandedCarId: string | null;
  editingCarId: string | null;
  editingCarData: Partial<IAutoparkCar>;
  onCarToggle: (carId: string) => void;
  onEditToggle: (car: IAutoparkCar) => void;
  onSaveChanges: (carId: string) => void;
  onFieldChange: (field: keyof IAutoparkCar, value: string | number) => void;
  onPhotoClick: (carImages: Array<{ id: string; photoUrl: string }>, photoIndex: number, carNumber: string) => void;
  onPhotoUpload: (carId: string, photoType: string, file: File) => Promise<void>;
}

function CarTableRow({
  car,
  expandedCarId,
  editingCarId,
  editingCarData,
  onCarToggle,
  onEditToggle,
  onSaveChanges,
  onFieldChange,
  onPhotoClick,
  onPhotoUpload,
}: CarTableRowProps) {
  const user = useAppSelector((state) => state.user.user);
  const [carMarks, setCarMarks] = useState<string[]>([]);
  const [carModels, setCarModels] = useState<ICarModel[]>([]);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  // Получение марок автомобилей
  const fetchCarMarks = async () => {
    setLoadingMarks(true);
    try {
      const data = await CarService.getCarMarks();
      setCarMarks(data);
    } catch (error) {
      console.error('Ошибка при запросе марок:', error);
      setCarMarks([]);
    } finally {
      setLoadingMarks(false);
    }
  };

  // Получение моделей при изменении марки
  const fetchCarModels = async (mark: string) => {
    if (!mark.trim()) {
      setCarModels([]);
      return;
    }

    setLoadingModels(true);
    try {
      const data = await CarService.getCarModels(mark);
      setCarModels(data);
    } catch (error) {
      console.error('Ошибка при запросе моделей:', error);
      setCarModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Загружаем марки при входе в режим редактирования
  useEffect(() => {
    if (editingCarId === car.id) {
      fetchCarMarks();
    }
  }, [editingCarId, car.id]);

  // Обновляем список моделей при изменении марки в режиме редактирования
  useEffect(() => {
    if (editingCarId === car.id && editingCarData.mark) {
      fetchCarModels(editingCarData.mark);
    }
  }, [editingCarData.mark, editingCarId, car.id]);

  const handleMarkChange = (value: string) => {
    onFieldChange('mark', value);
    // Очищаем модель при изменении марки
    onFieldChange('model', '');
    // Очищаем список моделей
    setCarModels([]);
  };

  const handleDateChange = (field: keyof IAutoparkCar, value: string) => {
    // Конвертируем дату из yyyy-MM-dd в dd-MM-yyyy для отправки в родительский компонент
    const convertedDate = convertDateFromInputFormat(value);
    onFieldChange(field, convertedDate);
  };

  const handleCarNumberChange = (value: string) => {
    // Применяем маску для номера автомобиля
    const formattedNumber = formatCarNumber(value);
    onFieldChange('number', formattedNumber);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>, photoType: string) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        await onPhotoUpload(car.id, photoType, file);
        console.log(`Фото ${photoType} успешно загружено для автомобиля ${car.id}`);
      } catch (error) {
        console.error(`Ошибка при загрузке фото ${photoType}:`, error);
      }
    }
  };

  return (
    <div className="car-item">
      {/* Основная строка */}
      <div className={`car-row ${expandedCarId === car.id ? 'car-row--expanded' : ''}`}>
        <span className="car-cell car-cell--number">
          {editingCarId === car.id ? (
            <input
              type="text"
              value={editingCarData.number !== undefined ? editingCarData.number : car.number}
              onChange={(e) => handleCarNumberChange(e.target.value)}
              className="edit-input"
              placeholder="А 111 АА 111"
              maxLength={12}
            />
          ) : (
            car.number
          )}
        </span>
        <span className="car-cell car-cell--mark">
          {editingCarId === car.id ? (
            <div className="edit-mark-model">
              <select
                value={editingCarData.mark !== undefined ? editingCarData.mark : car.mark}
                onChange={(e) => handleMarkChange(e.target.value)}
                className="edit-input edit-input--mark"
                disabled={loadingMarks}
              >
                <option value="">Выберите марку</option>
                {carMarks.map((mark) => (
                  <option key={mark} value={mark}>
                    {mark}
                  </option>
                ))}
              </select>
              <select
                value={editingCarData.model !== undefined ? editingCarData.model : car.model}
                onChange={(e) => onFieldChange('model', e.target.value)}
                className="edit-input edit-input--model"
                disabled={!(editingCarData.mark !== undefined ? editingCarData.mark : car.mark) || loadingModels}
              >
                <option value="">Выберите модель</option>
                {carModels.map((model) => (
                  <option key={`${model.mark}-${model.model}`} value={model.model}>
                    {model.model}
                  </option>
                ))}
              </select>
              {(loadingMarks || loadingModels) && (
                <div className="loading-models">{loadingMarks ? 'Загрузка марок...' : 'Загрузка моделей...'}</div>
              )}
            </div>
          ) : (
            `${car.mark} ${car.model}`
          )}
        </span>
        <div className="car-cell car-cell--license">
          <span>
            Номер:{' '}
            {editingCarId === car.id ? (
              <input
                type="text"
                value={editingCarData.taxiLicense !== undefined ? editingCarData.taxiLicense : car.taxiLicense}
                onChange={(e) => onFieldChange('taxiLicense', e.target.value)}
                className="edit-input edit-input--small"
              />
            ) : (
              car.taxiLicense
            )}
          </span>
          <span>
            Дата:{' '}
            {editingCarId === car.id ? (
              <input
                type="date"
                value={formatDateForInput(editingCarData.issueDate || car.issueDate)}
                onChange={(e) => handleDateChange('issueDate', e.target.value)}
                className="edit-input edit-input--small"
              />
            ) : (
              car.issueDate
            )}
          </span>
        </div>
        <div className="car-cell car-cell--details">
          {user?.role === 'autopark_employee' && editingCarId === car.id ? (
            <button
              className="details-button"
              onClick={(e) => {
                e.stopPropagation();
                onSaveChanges(car.id);
              }}
            >
              Сохранить
            </button>
          ) : (
            <div className="action-buttons">
              {editingCarId !== car.id && (
                <button
                  className="details-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCarToggle(car.id);
                  }}
                >
                  Подробнее
                  <svg width="9" height="5" viewBox="0 0 9 5" fill="none">
                    <path d="M1 1L4.5 4.5L8 1" stroke="#007AFF" />
                  </svg>
                </button>
              )}
              {user?.role === 'autopark_employee' && (
                <button
                  className="edit-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditToggle(car);
                  }}
                >
                  Изменить
                  <svg width="9" height="5" viewBox="0 0 9 5" fill="none">
                    <path d="M1 1L4.5 4.5L8 1" stroke="#8A8A8A" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="car-separator-container">
        <div className="car-separator"></div>
      </div>

      {/* Развернутая информация */}
      {expandedCarId === car.id && (
        <div className="car-details">
          <div className="car-details__content">
            {/* Диагностическая карта */}
            <div className="document-section">
              <div className="document-header">
                {car.diagnosticCardVerified && (
                  <div className="check-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" fill="#D6DB30" fillOpacity="0.6" />
                      <path
                        d="M11.52 5.146C11.715 5.341 11.715 5.658 11.52 5.853L7.187 10.187C6.992 10.382 6.675 10.382 6.48 10.187L4.147 7.854C3.952 7.659 3.952 7.342 4.147 7.147C4.342 6.952 4.659 6.952 4.854 7.147L6.833 9.126L10.146 5.813L10.813 5.146C11.008 4.951 11.325 4.951 11.52 5.146Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                )}
                <span className="document-title">Диагностическая карта</span>
              </div>
              <div className="document-info">
                <div className="info-item">
                  <span className="info-label">Действует до:</span>
                  <span className="info-value">
                    {editingCarId === car.id ? (
                      <input
                        type="date"
                        value={formatDateForInput(editingCarData.validityPeriodOfDC || car.validityPeriodOfDC || '')}
                        onChange={(e) => handleDateChange('validityPeriodOfDC', e.target.value)}
                        className="edit-input edit-input--small"
                      />
                    ) : (
                      car.validityPeriodOfDC || ''
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Номер:</span>
                  <span className="info-value">
                    {editingCarId === car.id ? (
                      <input
                        type="number"
                        value={
                          editingCarData.diagnosticCardNumber !== undefined
                            ? editingCarData.diagnosticCardNumber
                            : car.diagnosticCardNumber || ''
                        }
                        onChange={(e) => onFieldChange('diagnosticCardNumber', +e.target.value)}
                        className="edit-input edit-input--small"
                      />
                    ) : (
                      car.diagnosticCardNumber || ''
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Фото документа */}
            <div className="photo-circle">
              <div className="photo-placeholder">
                {editingCarId === car.id ? (
                  <label className="photo-upload-label" htmlFor={`diagnostic-card-${car.id}`}>
                    <div className="photo-upload-container">
                      {car.diagnosticCardPhoto ? (
                        <img src={normalizeImageUrl(car.diagnosticCardPhoto)} alt="Фото документа" />
                      ) : (
                        <div className="photo-placeholder-small">
                          <svg width="46" height="40" viewBox="0 0 46 40" fill="none"></svg>
                        </div>
                      )}
                      <div className="photo-upload-overlay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 15.5L7.5 11H10.5V4H13.5V11H16.5L12 15.5Z" fill="white" />
                          <path
                            d="M6 18V20H18V18H20V20C20 21.1 19.1 22 18 22H6C4.9 22 4 21.1 4 20V18H6Z"
                            fill="white"
                          />
                        </svg>
                      </div>
                    </div>
                    <input
                      type="file"
                      id={`diagnostic-card-${car.id}`}
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'diagnostic-card')}
                      style={{ display: 'none' }}
                    />
                  </label>
                ) : car.diagnosticCardPhoto ? (
                  <img
                    src={normalizeImageUrl(car.diagnosticCardPhoto)}
                    alt="Фото документа"
                    onClick={() =>
                      onPhotoClick(
                        [{ id: car.id, photoUrl: normalizeImageUrl(car.diagnosticCardPhoto) || '' }],
                        0,
                        car.number,
                      )
                    }
                  />
                ) : (
                  <div className="photo-placeholder-small">
                    <svg width="46" height="40" viewBox="0 0 46 40" fill="none"></svg>
                  </div>
                )}
              </div>
            </div>

            {/* ОСАГО */}
            <div className="document-section">
              <div className="document-title underlined">ОСАГО</div>
              <div className="document-info">
                <div className="info-item">
                  <span className="info-label">Действует до:</span>
                  <span className="info-value">
                    {editingCarId === car.id ? (
                      <input
                        type="date"
                        value={formatDateForInput(
                          editingCarData.validityPeriodOfOsago || car.validityPeriodOfOsago || '',
                        )}
                        onChange={(e) => handleDateChange('validityPeriodOfOsago', e.target.value)}
                        className="edit-input edit-input--small"
                      />
                    ) : (
                      car.validityPeriodOfOsago || ''
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Номер:</span>
                  <span className="info-value">
                    {editingCarId === car.id ? (
                      <input
                        type="string"
                        value={
                          editingCarData.osagoNumber !== undefined
                            ? editingCarData.osagoNumber || ''
                            : car.osagoNumber || ''
                        }
                        onChange={(e) => onFieldChange('osagoNumber', e.target.value)}
                        className="edit-input edit-input--small"
                      />
                    ) : (
                      `${car.osagoNumber || ''} `
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Фото документа */}
            <div className="photo-circle">
              <div className="photo-placeholder">
                {editingCarId === car.id ? (
                  <label className="photo-upload-label" htmlFor={`osago-${car.id}`}>
                    <div className="photo-upload-container">
                      {car.osagoPhoto ? (
                        <img src={normalizeImageUrl(car.osagoPhoto)} alt="Фото документа" />
                      ) : (
                        <div className="photo-placeholder-small">
                          <svg width="46" height="40" viewBox="0 0 46 40" fill="none"></svg>
                        </div>
                      )}
                      <div className="photo-upload-overlay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 15.5L7.5 11H10.5V4H13.5V11H16.5L12 15.5Z" fill="white" />
                          <path
                            d="M6 18V20H18V18H20V20C20 21.1 19.1 22 18 22H6C4.9 22 4 21.1 4 20V18H6Z"
                            fill="white"
                          />
                        </svg>
                      </div>
                    </div>
                    <input
                      type="file"
                      id={`osago-${car.id}`}
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'osago')}
                      style={{ display: 'none' }}
                    />
                  </label>
                ) : car.osagoPhoto ? (
                  <img
                    src={normalizeImageUrl(car.osagoPhoto)}
                    alt="Фото документа"
                    onClick={() => onPhotoClick([{ id: car.id, photoUrl: car.osagoPhoto || '' }], 0, car.number)}
                  />
                ) : (
                  <div className="photo-placeholder-small">
                    <svg width="46" height="40" viewBox="0 0 46 40" fill="none"></svg>
                  </div>
                )}
              </div>
            </div>

            {/* Фото кузова */}
            <div className="body-photos-section">
              <div className="document-title underlined">Кузов:</div>
              <div className="body-photos">
                {Array.from({ length: 5 }, (_, imgIndex) => {
                  const image = car?.carImages?.[imgIndex] || { id: null, photoUrl: null };
                  return (
                    <div key={image.id || `empty-${imgIndex}`} className="photo-circle">
                      {editingCarId === car.id ? (
                        <label className="photo-upload-label" htmlFor={`body-photo-${car.id}-${imgIndex}`}>
                          <div className="photo-upload-container">
                            {image.photoUrl ? (
                              <img src={normalizeImageUrl(image.photoUrl)} alt={`Фото кузова ${imgIndex + 1}`} />
                            ) : (
                              <div className="photo-placeholder-small"></div>
                            )}
                            <div className="photo-upload-overlay">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 15.5L7.5 11H10.5V4H13.5V11H16.5L12 15.5Z" fill="white" />
                                <path
                                  d="M6 18V20H18V18H20V20C20 21.1 19.1 22 18 22H6C4.9 22 4 21.1 4 20V18H6Z"
                                  fill="white"
                                />
                              </svg>
                            </div>
                          </div>
                          <input
                            type="file"
                            id={`body-photo-${car.id}-${imgIndex}`}
                            accept="image/*"
                            onChange={(e) => handlePhotoUpload(e, `body-photo-${imgIndex}`)}
                            style={{ display: 'none' }}
                          />
                        </label>
                      ) : (
                        <div onClick={() => image.photoUrl && onPhotoClick(car.carImages, imgIndex, car.number)}>
                          {image.photoUrl ? (
                            <img src={normalizeImageUrl(image.photoUrl)} alt={`Фото кузова ${imgIndex + 1}`} />
                          ) : (
                            <div className="photo-placeholder-small"></div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CarTableRow;
